import { HelpSection } from '../models/help-section.model';
import { serializeHelpDocument } from './help-document.util';
import { sanitizeHelpHtml, sanitizeHelpHtmlFragment } from './help-html-sanitize.util';

export type HelpDocumentUiMode = 'simple' | 'sections';

export interface HelpLangContentSnapshot {
  /** HTML serializado por idioma (columna BD) */
  htmlByLang: Record<string, string>;
}

/** Normaliza HTML de una columna de idioma para comparación estable */
export function normalizeHelpLangHtml(
  html: string | null | undefined,
  documentMode: HelpDocumentUiMode,
): string {
  const raw = (html ?? '').trim();
  if (!raw) return '';
  return documentMode === 'sections'
    ? sanitizeHelpHtml(raw)
    : sanitizeHelpHtmlFragment(raw);
}

/** Construye HTML de columna desde formulario simple o secciones */
export function buildHelpLangHtml(
  langId: string,
  documentMode: HelpDocumentUiMode,
  formValue: string | null | undefined,
  sectionsByLang: Record<string, HelpSection[]>,
): string {
  if (documentMode === 'sections') {
    const sections = sectionsByLang[langId] ?? [];
    if (!sections.length) return '';
    return normalizeHelpLangHtml(serializeHelpDocument(sections), 'sections');
  }
  return normalizeHelpLangHtml(String(formValue ?? ''), 'simple');
}

/**
 * Idiomas cuyo HTML difiere del snapshot inicial (p. ej. tras editar en CKEditor).
 */
export function getModifiedHelpLangIds(
  langIds: readonly string[],
  currentHtmlByLang: Record<string, string>,
  initialSnapshot: HelpLangContentSnapshot,
): string[] {
  return langIds.filter((langId) => {
    const current = currentHtmlByLang[langId] ?? '';
    const initial = initialSnapshot.htmlByLang[langId] ?? '';
    return current !== initial;
  });
}

/**
 * Idiomas a persistir: cualquier columna cuyo HTML difiera del snapshot inicial.
 * Incluye cambios solo en menú (data-title) sin editar el CKEditor.
 */
export function resolveLangsToPersist(
  langIds: readonly string[],
  _dirtyLangIds: ReadonlySet<string>,
  currentHtmlByLang: Record<string, string>,
  initialSnapshot: HelpLangContentSnapshot,
): string[] {
  return getModifiedHelpLangIds(langIds, currentHtmlByLang, initialSnapshot);
}
